import { AxiosError } from 'axios'
import { List, Map } from 'immutable'
import { AnyAction, Dispatch } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { v4 as uuidv4 } from 'uuid'

import { createRescheduleAppointmentPayload } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import { LW_API_ERROR_CODE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/errorCodes'
import {
  isV2AiConversation,
  mapConversationV2ToMessageConversation,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/conversations'
import {
  createPendingMessageV2,
  HIGH_ALERT_HISTORY_RETRY_DELAY_MS,
  isHighAlertRedResponseV2,
  mapChatHistoryResponseV2ToPage,
  mapChatResponseV2ToMessages,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/messagesV2'
import { GetOutcomesAction } from '@lyrahealth-inc/shared-app-logic/src/features/outcomes/types'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Feedback } from '@lyrahealth-inc/shared-app-logic/src/models/feedback/Feedback'
import { CONVERSATION_TYPE, MessageConversation } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import {
  CustomNotificationsPreferences,
  NotificationPreferences,
  UpdateNotificationPreferences,
} from '@lyrahealth-inc/shared-app-logic/src/models/notifications/Notifications'
import { SessionInfo } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/videoSessions/VideoSessions'

import * as LyraTherapyConstants from './LyraTherapyConstants'
import LyraTherapyReducer from './LyraTherapyReducer'
import { setBannerAndErrorMessage } from '../../../common/components/banner/banner-actions'
import { noOpActionErrorHandler } from '../../../common/http/actionErrorHandler'
import { editAppointmentByUserId, patchAppointmentByUserId } from '../../../common/http/data/appointmentDashboard'
import {
  deleteCustomNotificationsData,
  getAccessTokenData,
  getAppointmentsByProviderData,
  getAppointmentsForClientData,
  getChildEpisodesData,
  getCustomNotificationsData,
  getDummyVideoAppointmentData,
  getFeedbackData,
  getMessageConversationsData,
  getMessageConversationsV2Data,
  getMessageData,
  getMessagesData,
  getMessagesV2Data,
  getNotificationPreferencesData,
  getOutcomesData,
  getProvidersData,
  getTokenData,
  getVideoSessionData,
  ltNoop,
  submitCustomNotificationsData,
  submitMessageData,
  SubmitMessageData,
  submitMessageV2Data,
  submitNotificationPreferencesData,
  updateUnreadMessageCountData,
  updateUnreadMessageCountV2Data,
} from '../../../common/http/data/lyraTherapy'
import { toggleIsSessionTimerDisabled } from '../../../common/utils/userUtils'
import { Client } from '../../../data/messenger/twilio-conversations'
import { deleteAppointment } from '../../appointmentDashboard/data/appointmentDashboardActions'

type IAppState = ReturnType<typeof LyraTherapyReducer>
export type ApplicationDispatch = ThunkDispatch<IAppState, void, AnyAction> & Dispatch

export const setDashboardFetched = (value: boolean) => ({
  type: 'SET_DASHBOARD_FETCHED',
  value,
})

export function markDashboardAsStale() {
  return (dispatch: ApplicationDispatch) => {
    dispatch(setDashboardFetched(false))
  }
}

export function getToken(ids: { apptID: string; providerID: string; patientID: string; sessionNumber: number }) {
  const request = getTokenData(ids)

  return (dispatch: ApplicationDispatch) => {
    return new Promise(function (resolve, reject) {
      request.then(
        (tokenReturn) => {
          dispatch({
            type: LyraTherapyConstants.GET_VIDEO_CALL_TOKEN,
            data: tokenReturn.data,
          })

          resolve(tokenReturn.data)
        },
        (error) => {
          dispatch(setBannerAndErrorMessage(error, true))
          reject(error)
        },
      )
    })
  }
}

// Gets the video session corresponding to the apptId parameter.
// This is currently used as the equivalent of a no-op to keep the session alive and is not used in any other context -
// hence the lack of reducer handling, etc. from this action handler.
export const getVideoSession = ({ apptId, patientId }: { apptId: string; patientId: string }) => {
  return () => {
    return getVideoSessionData({ apptId, patientId })
  }
}

export const toggleVideoSessionStatus = (toggle: boolean) => {
  toggleIsSessionTimerDisabled(toggle)
  return {
    type: LyraTherapyConstants.VIDEO_SESSION_STATUS_UPDATE,
    payload: toggle,
  }
}

export const submitFeedback = (data: Feedback, hideError?: boolean) => ({
  action: LyraTherapyConstants.SUBMIT_FEEDBACK,
  request: {
    method: 'post',
    url: '/fdbks/v1/feedback',
    data,
  },
  ...(hideError ? { errorHandler: noOpActionErrorHandler } : {}),
})

export const updateFeedbackResponse = (data: { feedbackResponseId: string; answer: string }, hideError?: boolean) => ({
  action: LyraTherapyConstants.UPDATE_FEEDBACK_RESPONSE,
  request: {
    method: 'patch',
    url: `/lt/v1/feedback_response/${data.feedbackResponseId}`,
    data: { answer: data.answer },
  },
  ...(hideError ? { errorHandler: noOpActionErrorHandler } : {}),
})

export const deleteFeedback = (id: string, hideError?: boolean) => ({
  action: LyraTherapyConstants.DELETE_FEEDBACK,
  request: {
    method: 'delete',
    url: `/fdbks/v1/feedback/${id}`,
  },
  ...(hideError ? { errorHandler: noOpActionErrorHandler } : {}),
})

export const getFeedback = (data: { lyraId: string; entityType: string }) => {
  const { lyraId, entityType } = data
  return async (dispatch: ApplicationDispatch) => {
    try {
      const feedback = await getFeedbackData({ lyraId, entityType })
      return dispatch({
        type: LyraTherapyConstants.GET_FEEDBACK,
        payload: {
          entityType,
          feedback: feedback.data,
        },
      })
    } catch (error) {
      console.error('Failed to get feedback', error)
      return error
    }
  }
}

// temp for LTV dev
export function getDummyVideoAppointment(id: string, data: object) {
  const request = getDummyVideoAppointmentData(id, data)
  return (dispatch: ApplicationDispatch) => {
    return new Promise(function (resolve, reject) {
      request.then(
        (returnObj) => {
          dispatch({
            type: LyraTherapyConstants.GET_LT_APPOINTMENTS,
            data: returnObj.data,
            appointmentType: 'video',
          })
          resolve(returnObj.data)
        },
        (error) => {
          dispatch(setBannerAndErrorMessage(error, true))
          reject(error)
        },
      )
    })
  }
}

export function getLTAppointmentsByProvider(data: { patientId: string; providerId: string; type: string }) {
  const request = getAppointmentsByProviderData(data)
  return (dispatch: ApplicationDispatch) => {
    return new Promise(function (resolve, reject) {
      request.then(
        (returnObj) => {
          dispatch({
            type: LyraTherapyConstants.GET_LT_APPOINTMENTS_BY_PROVIDER,
            data: returnObj.data,
            appointmentType: data.type,
          })
          resolve(returnObj.data)
        },
        (error) => {
          dispatch(setBannerAndErrorMessage(error, true))
          reject(error)
        },
      )
    })
  }
}

export function getLTAppointmentsForClient(data: { patientId: string; type: string }) {
  const request = getAppointmentsForClientData(data)
  return (dispatch: ApplicationDispatch) => {
    return new Promise(function (resolve, reject) {
      request.then(
        (returnObj) => {
          dispatch({
            type: LyraTherapyConstants.GET_LT_APPOINTMENTS_FOR_CLIENT,
            data: returnObj.data,
            appointmentType: data.type,
          })
          resolve(returnObj.data)
        },
        (error) => {
          dispatch(setBannerAndErrorMessage(error, true))
          reject(error)
        },
      )
    })
  }
}

export const updateVideoSessionSettings = (settingsObj: {
  videoInput: string
  audioInput: string
  audioOutput: string
}) => {
  return {
    type: LyraTherapyConstants.VIDEO_SESSION_SETTINGS_UPDATE,
    payload: settingsObj,
  }
}

export const toggleVideoSessionModal = (bool: boolean) => {
  return {
    type: LyraTherapyConstants.TOGGLE_VIDEO_SESSION_MODAL,
    payload: bool,
  }
}

export const setAppointment = ({ appointment }: { appointment: Appointment }) => {
  return {
    appointment,
    type: LyraTherapyConstants.SET_APPOINTMENT,
  }
}

export const setAppointmentReschedule = ({
  appointment,
  patientLyraId,
}: {
  appointment: Appointment
  patientLyraId?: string
}) => {
  return {
    appointment,
    patientLyraId,
    type: LyraTherapyConstants.SET_APPOINTMENT_RESCHEDULE,
  }
}

export const updateAppointment = ({ userId, appointment }: { userId: string; appointment: Map<string, unknown> }) => {
  return () => {
    return editAppointmentByUserId({
      userId,
      // @ts-expect-error TS(2740): Type '{ address: any; appointmentDuration: any; ap... Remove this comment to see the full error message
      data: createRescheduleAppointmentPayload({
        appointment,
        userID: userId,
        referenceAppointment: Map(),
      }),
    })
  }
}

export const patchAppointment = ({
  userId,
  appointmentId,
  partialAppointment,
}: {
  userId: string
  appointmentId: number
  partialAppointment: Partial<Appointment>
}) => {
  return () => {
    return patchAppointmentByUserId({ userId, appointmentId, partialAppointment })
  }
}

export const cancelAppointment = ({ appointmentId }: { appointmentId: string }) => {
  return (dispatch: ApplicationDispatch) => {
    return dispatch(deleteAppointment(appointmentId))
  }
}

export const clearSessionStore = () => {
  return {
    type: LyraTherapyConstants.CLEAR_SESSION_STORE,
  }
}

export const clearSelectedAppointment = () => {
  return {
    type: LyraTherapyConstants.CLEAR_SELECTED_APPOINTMENT,
  }
}

export const showSessionCancellationModal = () => {
  return {
    type: LyraTherapyConstants.SHOW_SESSION_CANCELLATION_MODAL,
  }
}

export const hideSessionCancellationModal = () => {
  return {
    type: LyraTherapyConstants.HIDE_SESSION_CANCELLATION_MODAL,
  }
}

export const getNotificationPreferences = ({ lyraId }: { lyraId: string }) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      const preferences = await getNotificationPreferencesData({ lyraId })
      return dispatch({
        type: LyraTherapyConstants.GET_NOTIFICATION_PREFERENCES,
        preferences,
      })
    } catch (err) {
      return err
    }
  }
}

export const deleteCustomNotificationsPreferences = ({ lyraId }: { lyraId: string }) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      await deleteCustomNotificationsData({ lyraId })
      return dispatch({
        type: LyraTherapyConstants.DELETE_CUSTOM_NOTIFICATION_PREFERENCES,
      })
    } catch (err) {
      return err
    }
  }
}

export const getCustomNotificationsPreferences = ({ lyraId }: { lyraId: string }) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      const preferences = await getCustomNotificationsData({ lyraId })
      return dispatch({
        type: LyraTherapyConstants.GET_CUSTOM_NOTIFICATION_PREFERENCES,
        preferences,
      })
    } catch (err) {
      return err
    }
  }
}

type MessageConversationSource = 'v1' | 'v2'

type MessageConversationRequestContext = {
  clientId: string
  providerId: string | null
  source: MessageConversationSource
  generation: number
}

let messageConversationRequestContext: MessageConversationRequestContext | undefined
let messageConversationSourceGeneration = 0

export const getMessageConversations = (
  providerId: string | null,
  clientId: string,
  isTwilioDeprecatedForLyraAI = false,
) => {
  return async (dispatch: ApplicationDispatch) => {
    // Preserve same-source refresh behavior, but do not let an obsolete source or client
    // update the shared conversation list after its replacement request has started.
    const source: MessageConversationSource = isTwilioDeprecatedForLyraAI ? 'v2' : 'v1'
    if (
      messageConversationRequestContext?.clientId !== clientId ||
      messageConversationRequestContext.providerId !== providerId ||
      messageConversationRequestContext.source !== source
    ) {
      messageConversationRequestContext = {
        clientId,
        providerId,
        source,
        generation: ++messageConversationSourceGeneration,
      }
    }
    const requestGeneration = messageConversationRequestContext.generation
    const shouldHandleResponse = () => messageConversationRequestContext?.generation === requestGeneration

    try {
      if (!isTwilioDeprecatedForLyraAI) {
        const conversation = await getMessageConversationsData(providerId, clientId)
        if (!shouldHandleResponse()) return
        return dispatch({
          type: LyraTherapyConstants.GET_MESSAGE_CONVERSATION,
          data: conversation.data,
        })
      }

      const [providerConversationsResult, aiCoachConversationsResult] = await Promise.allSettled([
        getMessageConversationsData(providerId, clientId, CONVERSATION_TYPE.PROVIDER).then(
          (providerConversations) => providerConversations.data,
        ),
        getMessageConversationsV2Data(clientId).then((aiCoachConversations) =>
          aiCoachConversations.data.map(mapConversationV2ToMessageConversation),
        ),
      ])

      if (!shouldHandleResponse()) return

      const failedResult =
        providerConversationsResult.status === 'rejected'
          ? providerConversationsResult
          : aiCoachConversationsResult.status === 'rejected'
            ? aiCoachConversationsResult
            : undefined

      const mergeAction = {
        type: LyraTherapyConstants.MERGE_MESSAGE_CONVERSATIONS,
        providerConversations:
          providerConversationsResult.status === 'fulfilled' ? providerConversationsResult.value : undefined,
        aiCoachConversations:
          aiCoachConversationsResult.status === 'fulfilled' ? aiCoachConversationsResult.value : undefined,
      }

      if (failedResult) {
        if (providerConversationsResult.status === 'fulfilled' || aiCoachConversationsResult.status === 'fulfilled') {
          dispatch(mergeAction)
        }
        dispatch(setBannerAndErrorMessage(failedResult.reason, true))
        return failedResult.reason
      }

      return dispatch(mergeAction)
    } catch (error) {
      if (!shouldHandleResponse()) return
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      dispatch(setBannerAndErrorMessage(error, true))
      return error
    }
  }
}

export const selectMessengerProvider = (providerId: string) => {
  return {
    type: LyraTherapyConstants.SELECT_MESSENGER_PROVIDER,
    data: providerId,
  }
}

export const selectMessengerConversation = (conversationId: string, providerId: string) => {
  return {
    type: LyraTherapyConstants.SELECT_MESSENGER_CONVERSATION,
    data: { conversationId, providerId },
  }
}

export const deselectMessengerProvider = () => {
  return {
    type: LyraTherapyConstants.DESELECT_MESSENGER_PROVIDER,
  }
}

export const setNotificationPreferences = (preferences: NotificationPreferences) => {
  return {
    type: LyraTherapyConstants.SET_NOTIFICATION_PREFERENCES,
    preferences,
  }
}

export const setShowSessionNotificationPrompt = (value: boolean) => {
  return {
    type: LyraTherapyConstants.SET_SHOW_SESSION_NOTIFICATION_PROMPT,
    value,
  }
}

export const submitNotificationPreferences = ({
  lyraId,
  data,
}: {
  lyraId: string
  data: UpdateNotificationPreferences
}) => {
  return (dispatch: ApplicationDispatch) => {
    return submitNotificationPreferencesData({ lyraId, data }).then(
      (success) => {
        dispatch(setNotificationPreferences(success.data))
        return Promise.resolve(success)
      },
      (error) => {
        return Promise.reject(error)
      },
    )
  }
}

export const setCustomNotificationsPreferences = ({
  lyraId,
  preferences,
}: {
  lyraId: string
  preferences: CustomNotificationsPreferences
}) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      await submitCustomNotificationsData({ lyraId, data: preferences })
      dispatch({
        type: LyraTherapyConstants.SET_CUSTOM_NOTIFICATION_PREFERENCES,
        preferences,
      })
    } catch (error: any) {
      dispatch(setBannerAndErrorMessage(error, true))
    }
  }
}

let messageHistoryRequestSequence = 0

export const getMessages = (conversation: MessageConversation, before?: string) => {
  return async (dispatch: ApplicationDispatch) => {
    const conversationId = conversation.conversation_id
    const requestId = before ? undefined : `${conversationId}:${++messageHistoryRequestSequence}`
    if (requestId) {
      dispatch({
        type: LyraTherapyConstants.GET_MESSAGES_REQUESTED,
        conversationId,
        requestId,
      })
    }

    try {
      if (isV2AiConversation(conversation)) {
        const response = await getMessagesV2Data(conversationId, before)
        const page = mapChatHistoryResponseV2ToPage(response.data, conversationId)

        dispatch({
          type: before ? LyraTherapyConstants.PREPEND_MESSAGES : LyraTherapyConstants.GET_MESSAGES,
          data: before
            ? page
            : {
                conversation_messages: page.messages,
                hasMore: page.hasMore,
                before: page.before,
              },
          conversationId,
          ...(before ? { requestedBefore: before } : {}),
          ...(requestId ? { requestId } : {}),
        })
        return page
      }

      const messages = await getMessagesData(conversationId)
      dispatch({
        type: LyraTherapyConstants.GET_MESSAGES,
        data: messages.data,
        conversationId,
        requestId,
      })
      return messages.data
    } catch (error) {
      if (requestId) {
        dispatch({
          type: LyraTherapyConstants.GET_MESSAGES_REQUEST_FAILED,
          conversationId,
          requestId,
        })
      }
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      dispatch(setBannerAndErrorMessage(error, true))
      return undefined
    }
  }
}

export const fetchMessage = ({ conversationId, messageId }: { conversationId: string; messageId: string }) => {
  // @ts-expect-error TS(7030): Not all code paths return a value.
  return async (dispatch: ApplicationDispatch | (() => void)) => {
    try {
      return await getMessageData(conversationId, messageId)
    } catch (error) {
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      dispatch(setBannerAndErrorMessage(error, true))
    }
  }
}

export const getMessage = ({ conversationId, messageId }: { conversationId: string; messageId: string }) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      const message = await getMessageData(conversationId, messageId)
      dispatch({
        type: LyraTherapyConstants.ADD_MESSAGE,
        data: message.data,
      })
    } catch (error) {
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      dispatch(setBannerAndErrorMessage(error, true))
      throw error
    }
  }
}

export const submitMessage = (conversationId: string, data: SubmitMessageData, useV2 = false) => {
  return async (dispatch: ApplicationDispatch) => {
    if (useV2) {
      const submittedMessage = {
        body: data.body,
        clientId: data.client_id,
        metadata: data.metadata,
      }
      const pendingMessage = createPendingMessageV2(
        conversationId,
        submittedMessage,
        uuidv4(),
        new Date().toISOString(),
      )

      dispatch({
        type: LyraTherapyConstants.ADD_MESSAGE,
        data: pendingMessage,
        conversationId,
      })

      try {
        const response = await submitMessageV2Data(conversationId, data)

        // RESET CONTEXT creates a new conversation on the backend. If the returned
        // conversation_id differs from the one we sent, update our stored ID so subsequent
        // requests (e.g. PATCH unread counts) target the correct conversation.
        const newConversationId = response.data.message?.conversation_id
        const effectiveConversationId =
          newConversationId && newConversationId !== conversationId ? newConversationId : conversationId
        if (newConversationId && newConversationId !== conversationId) {
          dispatch(
            updateSelectedConversationId({
              oldConversationId: conversationId,
              newConversationId,
            }),
          )
        }

        const [clientMessage, assistantMessage] = mapChatResponseV2ToMessages(
          response.data,
          submittedMessage,
          pendingMessage,
        )

        dispatch({
          type: LyraTherapyConstants.COMPLETE_PENDING_MESSAGE,
          conversationId: effectiveConversationId,
          pendingMessageId: pendingMessage.message_id,
          clientMessage: {
            ...clientMessage,
            conversationId: effectiveConversationId,
          },
          assistantMessage: {
            ...assistantMessage,
            conversationId: effectiveConversationId,
          },
        })
        if (isHighAlertRedResponseV2(response.data)) {
          dispatch(lockV2HighAlertConversation(conversationId))
        }
        return response.data
      } catch (error) {
        dispatch({
          type: LyraTherapyConstants.REMOVE_PENDING_MESSAGE,
          conversationId,
          pendingMessageId: pendingMessage.message_id,
        })
        // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        dispatch(setBannerAndErrorMessage(error, true))
        throw error
      }
    }

    try {
      const message = await submitMessageData(conversationId, data)
      dispatch({
        type: LyraTherapyConstants.ADD_MESSAGE,
        data: message.data,
        conversationId,
      })
      return message.data
    } catch (error) {
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      dispatch(setBannerAndErrorMessage(error, true))
      throw error
    }
  }
}

export const lockV2HighAlertConversation = (conversationId: string) => ({
  type: LyraTherapyConstants.LOCK_V2_HIGH_ALERT_CONVERSATION,
  conversationId,
})

export const setV2HighAlertTimeoutDeadline = (conversationId: string, deadline: number) => ({
  type: LyraTherapyConstants.SET_V2_HIGH_ALERT_TIMEOUT_DEADLINE,
  conversationId,
  deadline,
})

export const clearV2HighAlertTimeoutDeadline = (conversationId: string) => ({
  type: LyraTherapyConstants.CLEAR_V2_HIGH_ALERT_TIMEOUT_DEADLINE,
  conversationId,
})

export const resyncV2HighAlertConversation = (conversationId: string, clientId: string) => {
  return async (dispatch: ApplicationDispatch, getState: () => Map<string, any>) => {
    dispatch(clearV2HighAlertTimeoutDeadline(conversationId))
    const conversation = (getState().getIn(['lyraTherapy', 'messenger', 'conversations'], List()) as List<any>)
      .find(($$conversation: Map<string, any>) => $$conversation.get('conversation_id') === conversationId)
      ?.toJS() as MessageConversation | undefined
    if (!conversation) {
      await dispatch(getMessageConversations(null, clientId, true))
      return
    }
    const [, historyPage] = await Promise.all([
      dispatch(getMessageConversations(null, clientId, true)),
      dispatch(getMessages(conversation)),
    ])
    if (!historyPage) {
      await new Promise((resolve) => setTimeout(resolve, HIGH_ALERT_HISTORY_RETRY_DELAY_MS))
      await dispatch(getMessages(conversation))
    }
  }
}

export const updateUnreadMessageCount = (
  conversationId: string,
  data: { unread_patient_messages_count: number } | { unread_ai_coach_messages_count: number },
) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      const count = await updateUnreadMessageCountData(conversationId, data)
      dispatch({
        type: LyraTherapyConstants.UPDATE_UNREAD_MESSAGE_COUNT,
        data: count.data,
        conversationId,
      })
    } catch (error) {
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      dispatch(setBannerAndErrorMessage(error, true))
    }
  }
}

export const updateUnreadMessageCountV2 = (
  conversationId: string,
  data: { unread_ai_coach_messages_count: number },
) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      const conversation = await updateUnreadMessageCountV2Data(conversationId, data)
      dispatch({
        type: LyraTherapyConstants.UPDATE_V2_UNREAD_MESSAGE_COUNT,
        data: mapConversationV2ToMessageConversation(conversation.data).conversation_attributes,
        conversationId,
      })
    } catch (error) {
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      dispatch(setBannerAndErrorMessage(error, true))
    }
  }
}

export const incrementUnreadMessageCount = (conversationId: string) => {
  return {
    type: LyraTherapyConstants.INCREMENT_UNREAD_MESSAGE_COUNT,
    conversationId,
  }
}

export const setTwilioClient = () => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      const res = await getAccessTokenData()
      const client = Client(res.data)
      return dispatch({
        type: LyraTherapyConstants.SET_CONVERSATIONS_CLIENT,
        data: client,
      })
    } catch (error) {
      return error
    }
  }
}

export const updateSelectedConversationId = (data: { oldConversationId: string; newConversationId: string }) => {
  return {
    type: LyraTherapyConstants.UPDATE_SELECTED_CONVERSATION_ID,
    data,
  }
}

export const getCurrentEpisodes = ({ lyraId }: { lyraId: string }) => ({
  action: LyraTherapyConstants.GET_CURRENT_EPISODES,
  request: {
    method: 'get',
    url: `/lt/v1/patient/${lyraId}/episodes/current`,
  },
  errorHandler: noOpActionErrorHandler,
})

export const getEpisodes = ({ lyraId }: { lyraId: string }) => ({
  action: LyraTherapyConstants.SET_EPISODES,
  request: {
    method: 'get',
    url: `/lt/v1/patient/${lyraId}/episodes`,
  },
  errorHandler: noOpActionErrorHandler,
})

export const getPrograms = () => ({
  action: LyraTherapyConstants.SET_PROGRAMS,
  request: {
    method: 'get',
    url: '/lt/v1/programs',
  },
})

export const getOutcomes = (data: GetOutcomesAction) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      const outcomes = await getOutcomesData(data)
      dispatch({
        type: LyraTherapyConstants.SET_OUTCOMES,
        data: outcomes.data,
      })
      return outcomes.data
    } catch (err) {
      const error = err as AxiosError<{ message?: string; errorCode?: LW_API_ERROR_CODE }>
      dispatch(setBannerAndErrorMessage(error, true))
      return error
    }
  }
}

export const getProviders = ({ clientId, includeChild }: { clientId: string; includeChild?: boolean }) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      const providers = await getProvidersData(clientId, includeChild)
      dispatch({
        type: LyraTherapyConstants.GET_PROVIDERS,
        data: providers.data,
      })
      return providers.data
    } catch (error) {
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      dispatch(setBannerAndErrorMessage(error, true))
      return null
    }
  }
}

export const getChildEpisodes = ({ userLyraId, childLyraId }: { userLyraId: string; childLyraId: string }) => {
  return async (dispatch: ApplicationDispatch) => {
    try {
      const episodes = await getChildEpisodesData(userLyraId, childLyraId)
      dispatch({
        type: LyraTherapyConstants.GET_CHILD_EPISODES,
        data: {
          childLyraId,
          episodes: episodes.data,
        },
      })
      // Returned so callers can consume the fetched episodes directly while the store stays the
      // shared source of truth (avoids callers issuing a second, store-bypassing fetch).
      return episodes.data
    } catch (error) {
      console.error('Failed to get episodes for child', error)
      return null
    }
  }
}

export const pingLT = () => () => ltNoop()

export const saveMessageDraft = (data: { content: string; conversationId: string }) => ({
  type: LyraTherapyConstants.SAVE_MESSAGE_DRAFT,
  data,
})

export const toggleLiveMsgSession = (bool: boolean) => ({
  type: LyraTherapyConstants.TOGGLE_LIVE_MSG_SESSION,
  payload: bool,
})

export const toggleShownLiveMsgModal = (bool: boolean) => ({
  type: LyraTherapyConstants.TOGGLE_SHOWN_LIVE_MSG_MODAL,
  payload: bool,
})

export const setActiveLiveMsgSession = (data: SessionInfo) => {
  return {
    type: LyraTherapyConstants.SET_ACTIVE_LIVE_MSG_SESSION,
    payload: data,
  }
}

export const toggleShowLiveMsgModal = (bool: boolean) => ({
  type: LyraTherapyConstants.TOGGLE_SHOW_LIVE_MSG_MODAL,
  payload: bool,
})

export const setLiveMsgProviderIdToOpen = (data: string | null) => ({
  type: LyraTherapyConstants.SET_LIVE_MSG_PROVIDER_ID_TO_OPEN,
  payload: data,
})

export const setIntakeFormOpen = (bool: boolean) => ({
  type: LyraTherapyConstants.SET_INTAKE_FORM_OPEN,
  payload: bool,
})

export const setOpenAssignmentId = (assignmentId: boolean) => ({
  type: LyraTherapyConstants.SET_OPEN_ASSIGNMENT_ID,
  payload: assignmentId,
})

export const setFirstAppointmentBooked = (value: boolean) => ({
  type: LyraTherapyConstants.SET_FIRST_APPOINTMENT_BOOKED,
  payload: value,
})

export const setViewedNotificationModal = (value: boolean) => ({
  type: LyraTherapyConstants.SET_VIEWED_NOTIFICATION_MODAL,
  payload: value,
})

export const toggleMessagesPopover = (value: boolean) => ({
  type: LyraTherapyConstants.TOGGLE_MESSAGES_POPOVER,
  payload: value,
})

export const setAwaitingAiResponse = (value: boolean) => ({
  type: LyraTherapyConstants.SET_AWAITING_AI_RESPONSE,
  payload: value,
})
